import SEO from "@/components/SEO";
import { Link } from "react-router-dom";
import { webPageSchema, breadcrumbSchema } from "@/lib/organization-schema";
import {
  Home,
  UtensilsCrossed,
  Shirt,
  HeartPulse,
  FileText,
  Briefcase,
  GraduationCap,
  ClipboardList,
  Dumbbell,
  Users,
  Scale,
  ScrollText,
  FolderOpen,
  Handshake,
  DoorOpen,
  Building2,
  Info,
} from "lucide-react";

const GIVE_URL = "https://give.abbashouse.org";

const programs = [
  {
    id: "transition",
    badge: "Program 01",
    title: "90-Day Transition Program",
    tagline: "From crisis to stability — free, compassionate, structured support when our center opens.",
    eligibility: [
      "Currently unhoused or at imminent risk of homelessness",
      "No active violent criminal history",
      "Willingness to participate in case management and programming",
      "Commitment to the 90-day plan with regular check-ins",
      "Must be drug-free upon entry (we will assist candidates in entering rehab prior to enrollment)",
      "Consent to weekly drug testing and random searches of pods, belongings, and persons",
    ],
    drugPolicy: "Drug test failure or discovery of illegal substances or items violating our Community Covenants will result in immediate removal from the program — to protect the safety and well-being of our entire community.",
    benefits: [
      { Icon: Home, label: "Free Private Pod", desc: "Your own private sleeping pod with bed, seating, and TV for the full 90 days." },
      { Icon: UtensilsCrossed, label: "Full Meal Plan", desc: "Three daily meals will be provided through our community cafeteria — no cost to you." },
      { Icon: Shirt, label: "Clothing & Essentials", desc: "Access to donated clothing, toiletries, and hygiene supplies." },
      { Icon: HeartPulse, label: "Healthcare Access", desc: "Connections to free or low-cost healthcare and mental health services through Abba's Care." },
      { Icon: FileText, label: "Documentation Help", desc: "Assistance obtaining IDs, Social Security cards, and vital records — removing barriers to housing and employment." },
      { Icon: Briefcase, label: "Job Placement", desc: "Résumé building, interview coaching, and job placement partnerships with local employers." },
      { Icon: GraduationCap, label: "Education Pathways", desc: "Enrollment support for trade school, community college, and certification programs." },
      { Icon: ClipboardList, label: "Case Management", desc: "A dedicated case manager will guide your individual plan from day one through graduation." },
    ],
    apply: "Walk-ins will be welcome at any of our Community Housing Centers once they open. In the meantime, reach out to us through our contact page to express interest.",
    cta: "Learn About the 90-Day Program",
  },
  {
    id: "membership",
    badge: "Program 02",
    title: "Independent Membership",
    tagline: "$750/month — housing, meals, and support in one simple plan when our center opens.",
    eligibility: [
      "Graduates of the 90-Day Transition Program",
      "Employed or receiving verifiable income",
      "In good standing with community covenants",
      "Desire to remain in a supportive, community-centered environment",
    ],
    benefits: [
      { Icon: Home, label: "Private Pod", desc: "Continue in your private pod at a fraction of the cost of regular housing." },
      { Icon: UtensilsCrossed, label: "Cafeteria Access", desc: "Access to a cafeteria with reduced-priced meals with no markup. Mercy Meal Plans will be available to those who qualify for meal credits." },
      { Icon: HeartPulse, label: "Healthcare Access", desc: "Connections to free or low-cost healthcare and mental health services through Abba's Care." },
      { Icon: ClipboardList, label: "Continued Case Management", desc: "Optional ongoing support to help you reach your next goal — saving, homeownership, or career advancement." },
      { Icon: Dumbbell, label: "Full Amenity Access", desc: "Gym, laundry, community recreation areas, and office/computer space." },
      { Icon: Users, label: "Community", desc: "Remain in a stable, encouraging environment while you save and build toward full independence." },
      { Icon: HeartPulse, label: "Pay It Forward", desc: "A portion of your monthly dues will directly fund someone else's 90-Day Transition — helping the next person get back on their feet." },
    ],
    note: "At $750/month, members will pay far below regular housing — which often requires 3× the monthly rate as proof of income, making a place to lay your head out of reach even for working adults. Independent Membership will bridge that gap. A portion of every member's monthly dues will be used to pay it forward — funding someone else's free 90-Day Transition Program.",
    apply: "Independent Membership will be offered to all 90-Day Program graduates in good standing. Once our center opens, speak with your case manager during your final week of the program to transition seamlessly.",
    cta: "Learn More About Membership",
  },
  {
    id: "direct-entry",
    badge: "Program 03",
    title: "Direct Entry Program",
    tagline: "Already employed? When our center opens, skip the line and move in.",
    eligibility: [
      "Currently employed with verifiable income",
      "Unhoused or unable to afford standard housing",
      "No active violent criminal history",
      "Ability to pay the $750/month membership fee",
      "Individuals demonstrating extreme financial need on Social Security or Disability may receive reduced monthly dues and meal assistance on an as-needed basis.",
    ],
    benefits: [
      { Icon: Home, label: "Private Pod", desc: "Immediate access to a private pod — no 90-day program required." },
      { Icon: UtensilsCrossed, label: "Cafeteria Access", desc: "Access to a cafeteria with reduced-priced meals with no markup. Mercy Meal Plans will be available to those who qualify for meal credits." },
      { Icon: HeartPulse, label: "Healthcare Access", desc: "Connections to free or low-cost healthcare and mental health services through Abba's Care." },
      { Icon: Building2, label: "Community Center Access", desc: "Full access to gyms, laundry, recreation areas, office space, and all shared amenities." },
      { Icon: ClipboardList, label: "Optional Case Management", desc: "Case management will be available but not required — employed members will set their own pace." },
      { Icon: HeartPulse, label: "Pay It Forward", desc: "A portion of your monthly dues will directly fund someone else's 90-Day Transition — helping the next person get back on their feet." },
    ],
    note: "Many working adults are homeless not because of unemployment, but because landlords require income of 3× the monthly rent as a qualification — putting even modest apartments out of reach. Direct Entry will meet you where you are. A portion of every member's monthly dues will be used to pay it forward — funding someone else's free 90-Day Transition Program.",
    apply: "Once our center opens, contact us to schedule an intake assessment. Bring proof of income and valid ID. Availability will be based on current pod capacity.",
    cta: "Learn About Direct Entry",
  },
  {
    id: "second-chance",
    badge: "Program 04",
    title: "Abba's Second Chance",
    tagline: "A clean record. A fresh start. A real future.",
    eligibility: [
      "Current or former Abba's House community member",
      "Nonviolent criminal record (misdemeanor or felony)",
      "Completed sentence, probation, or parole",
      "Demonstrated commitment to rehabilitation and positive change",
    ],
    benefits: [
      { Icon: Scale, label: "Court Advocacy", desc: "We partner with local courts and public defenders to advocate for expungement eligibility for qualifying nonviolent offenses." },
      { Icon: ScrollText, label: "Governor's Pardon Support", desc: "Our legal partners assist members in preparing and submitting pardon applications to state Governor's Pardon Boards." },
      { Icon: FolderOpen, label: "Record Expungement", desc: "Guidance through the expungement process — clearing records that prevent housing applications, employment, and licensing." },
      { Icon: Handshake, label: "Legal Aid Partnerships", desc: "Connections to pro bono attorneys and legal aid organizations specializing in criminal record relief." },
      { Icon: Briefcase, label: "Employment Re-Entry", desc: "With a cleared record, members gain access to expanded job opportunities, professional licensing, and higher-wage careers." },
      { Icon: DoorOpen, label: "Housing Barriers Removed", desc: "Criminal records are a leading cause of housing rejections. Expungement opens doors to private rentals and homeownership." },
    ],
    note: "A past mistake should not define a person's entire future. Abba's Second Chance believes in redemption — and works to make it legally real. We are actively building partnerships with courts and Governor's Pardon Boards across the country to give our members the clean slate they've earned.",
    apply: "Once our center opens, speak with your case manager or any Abba's House staff member to begin a Second Chance assessment. We will review your record, identify eligibility, and connect you with the appropriate legal resources.",
    cta: "Start Your Second Chance",
  },
];

export default function Programs() {
  return (
    <main>
      <SEO
        title="Our Programs"
        description="Explore Abba's House programs: the 90-Day Transition Program, Independent Membership, Direct Entry for employed individuals, and Abba's Second Chance for nonviolent record expungement."
        canonical="https://abbashouse.org/programs"
        jsonLd={[
          webPageSchema({ name: "Our Programs — Abba's House Communities", description: "Explore Abba's House programs: 90-Day Transition, Independent Membership, Direct Entry, and Abba's Second Chance.", url: "https://abbashouse.org/programs" }),
          breadcrumbSchema([
            { name: "Home", url: "https://abbashouse.org/" },
            { name: "Programs", url: "https://abbashouse.org/programs" },
          ]),
        ]}
      />
      <section className="bg-primary text-primary-foreground py-24 md:py-32">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
          <p className="text-sm font-semibold tracking-[0.3em] uppercase opacity-50 mb-6">What We Will Offer</p>
          <h1 className="text-5xl sm:text-6xl md:text-7xl font-black tracking-tight leading-none mb-8 max-w-3xl">
            Programs Built for Real Life.
          </h1>
          <p className="text-lg sm:text-xl opacity-70 leading-relaxed max-w-2xl">
            Whether you're in crisis, employed but priced out, or rebuilding after a second chance — Abba's House will have a path designed for where you are. We are currently raising funds to make this vision a reality.
          </p>
        </div>
      </section>

      {/* Program Nav */}
      <section className="bg-secondary border-b border-border sticky top-16 md:top-20 z-30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <nav className="flex overflow-x-auto gap-0 scrollbar-none">
            {programs.map((p) => (
              <a
                key={p.id}
                href={`#${p.id}`}
                className="flex-shrink-0 px-5 py-4 text-sm font-semibold tracking-wide text-muted-foreground hover:text-foreground border-b-2 border-transparent hover:border-foreground transition-all whitespace-nowrap"
              >
                {p.title}
              </a>
            ))}
          </nav>
        </div>
      </section>

      {/* Programs */}
      {programs.map((program, idx) => (
        <section
          key={program.id}
          id={program.id}
          className={`py-20 md:py-28 scroll-mt-32 ${idx % 2 === 0 ? "bg-background" : "bg-secondary"}`}
        >
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
            {/* Header */}
            <div className="mb-14">
              <span className="inline-block text-xs font-bold tracking-[0.2em] uppercase text-muted-foreground mb-4">
                {program.badge}
              </span>
              <h2 className="text-4xl md:text-5xl font-black tracking-tight mb-3">{program.title}</h2>
              <p className="text-muted-foreground text-xl max-w-2xl">{program.tagline}</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
              {/* Benefits */}
              <div className="lg:col-span-2">
                <h3 className="text-xs font-bold tracking-[0.2em] uppercase text-muted-foreground mb-6">What's Included</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {program.benefits.map((b) => (
                    <div key={b.label} className="border border-border rounded-2xl p-5 hover:border-foreground transition-colors">
                      <b.Icon className="w-6 h-6 mb-3 text-primary" strokeWidth={1.5} />
                      <h4 className="font-bold mb-1">{b.label}</h4>
                      <p className="text-sm text-muted-foreground leading-relaxed">{b.desc}</p>
                    </div>
                  ))}
                </div>

                {/* Note */}
                {program.note && (
                  <div className="mt-6 border-l-4 border-primary pl-6 py-2">
                    <p className="text-muted-foreground leading-relaxed text-sm italic">{program.note}</p>
                  </div>
                )}

                {/* Drug Policy */}
                {"drugPolicy" in program && program.drugPolicy && (
                  <div className="mt-4 bg-destructive/10 border border-destructive/20 rounded-2xl p-5">
                    <p className="text-sm font-semibold text-destructive mb-1">Community Safety Policy</p>
                    <p className="text-sm text-muted-foreground leading-relaxed">{program.drugPolicy}</p>
                  </div>
                )}
              </div>

              {/* Sidebar: Eligibility + Apply */}
              <div className="flex flex-col gap-6">
                {/* Eligibility */}
                <div className="bg-secondary rounded-2xl p-6 border border-border">
                  <h3 className="text-xs font-bold tracking-[0.2em] uppercase text-muted-foreground mb-5">Eligibility</h3>
                  <ul className="flex flex-col gap-3">
                    {program.eligibility.map((item) => (
                      <li key={item} className="flex gap-3 items-start text-sm leading-relaxed">
                        {item.includes("extreme financial need") ? (
                          <Info className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                        ) : (
                          <span className="text-primary font-black mt-0.5 flex-shrink-0">✓</span>
                        )}
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* How to Apply */}
                <div className="bg-secondary rounded-2xl p-6 border border-border">
                  <h3 className="text-xs font-bold tracking-[0.2em] uppercase text-muted-foreground mb-4">How to Apply</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-5">{program.apply}</p>
                  <Link
                    to="/contact"
                    className="inline-flex items-center w-full justify-center px-6 py-3 rounded-full bg-primary text-primary-foreground text-sm font-bold tracking-wide hover:opacity-80 transition-opacity"
                  >
                    {program.cta}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Final CTA */}
      <section className="bg-primary text-primary-foreground py-20 md:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl text-center">
          <p className="text-sm font-semibold tracking-[0.3em] uppercase opacity-50 mb-6">Get Involved</p>
          <h2 className="text-4xl sm:text-5xl font-black tracking-tight mb-6">
            Know Someone Who Needs a Home?
          </h2>
          <p className="text-lg opacity-70 mb-10 max-w-2xl mx-auto">
            Share our programs, make a donation, or reach out directly. Every connection matters.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={GIVE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-10 py-5 rounded-full bg-primary-foreground text-primary text-lg font-bold tracking-wide hover:opacity-90 transition-opacity w-full sm:w-auto justify-center"
            >
              Give Now
            </a>
            <Link
              to="/partner"
              className="inline-flex items-center px-10 py-5 rounded-full border-2 border-primary-foreground text-primary-foreground text-lg font-semibold tracking-wide hover:bg-primary-foreground hover:text-primary transition-colors w-full sm:w-auto justify-center"
            >
              Partner With Us
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}